"use client";

import { memo, useMemo, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { CardContent } from "@/components/ui/card";
import BaseChartBlock from "../shared/BaseChartBlock";
import { StatCard } from "../ui/StatCard";
import type { TranscribeChartBlockProps } from "../types";
import type { SummaryContent, TranscribeCounts } from "@/types/endpoints/reports/GetAiReportById";
import { Divider } from "../ui/Divider";
import { ChevronDownIcon } from "lucide-react";

const TRANSCRIBE_LABEL_KEYS: Record<string, string> = {
  multiple_speakers_detected: "label-action-multiple-speakers-detected",
  dialogue_pattern: "label-action-dialogue-pattern",
  question_answer_exchange: "label-action-question-answer-exchange",
  confirmation_contradiction: "label-action-confirmation-contradiction",
  imperative_commands: "label-action-imperative-commands",
  group_instructions: "label-action-group-instructions",
  technical_discussion: "label-action-technical-discussion",
  system_manipulation_discussion: "label-action-system-manipulation-discussion",
  coordination_attempts: "label-action-coordination-attempts",
  task_division: "label-action-task-division",
  answer_sharing: "label-action-answer-sharing",
  solution_dictation: "label-action-solution-dictation",
  external_assistance_request: "label-action-external-assistance-request",
  hint_receiving: "label-action-hint-receiving",
  collaborative_celebration: "label-action-collaborative-celebration",
  frustration_expression_to_others: "label-action-frustration-expression-to-others",
  turn_taking: "label-action-turn-taking",
  cheating_keywords: "verdict-action-cheating_keywords",
  synchronization_phrases: "label-action-synchronization-phrases",
};

const TRANSCRIBE_COLORS: Record<string, string> = {
  system_manipulation_discussion: "#7F1D1D",
  answer_sharing: "#991B1B",
  solution_dictation: "#B91C1C",
  hint_receiving: "#DC2626",
  task_division: "#EF4444",
  external_assistance_request: "#F87171",
  multiple_speakers_detected: "#F59E0B",
  dialogue_pattern: "#F97316",
  question_answer_exchange: "#FB923C",
  group_instructions: "#FDBA74",
  technical_discussion: "#8B5CF6",
  imperative_commands: "#3B82F6",
  confirmation_contradiction: "#60A5FA",
  turn_taking: "#93C5FD",
  coordination_attempts: "#EC4899",
  synchronization_phrases: "#F472B6",
  collaborative_celebration: "#10B981",
  frustration_expression_to_others: "#6B7280",
};

const getSummaryContent = (summary: SummaryContent | undefined): string => {
  if (!summary) return "";
  if (typeof summary === "string") return summary;
  return summary.content ?? "";
};

const getTranscribeLabel = (actionType: string, t: (key: string) => string) => {
  const labelKey = TRANSCRIBE_LABEL_KEYS[actionType];
  return labelKey ? t(labelKey) : actionType;
};

const getTranscribeColor = (actionType: string) =>
  TRANSCRIBE_COLORS[actionType] || "#6B7280";

const formatTime = (seconds?: number | null) => {
  if (seconds == null || Number.isNaN(seconds)) return "00:00";
  const total = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const formatPercent = (value?: number | null) => {
  if (!value) return "0%";
  return `${Math.round(value * 100)}%`;
};

const polarToCartesian = (cx: number, cy: number, r: number, angle: number) => {
  const rad = ((angle - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
};

const describePieSlice = (
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number
) => {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return [
    `M ${cx} ${cy}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
};

const describeFullCircle = (cx: number, cy: number, radius: number) =>
  [
    `M ${cx - radius} ${cy}`,
    `a ${radius} ${radius} 0 1 0 ${radius * 2} 0`,
    `a ${radius} ${radius} 0 1 0 ${-radius * 2} 0`,
    "Z",
  ].join(" ");

const weightBadgeClass = (weight?: string) => {
  switch (weight) {
    case "high":
      return "bg-red-100 text-red-700";
    case "medium":
      return "bg-yellow-100 text-yellow-700";
    case "low":
      return "bg-green-100 text-green-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

export const TranscribeChartBlock = memo(function TranscribeChartBlock({
  attemptId,
  onOpenTranscribeDetail,
  onAnalyticsData,
}: TranscribeChartBlockProps) {
  const t = useTranslations();
  const locale = useLocale();
  const [activeTab, setActiveTab] = useState<"conclusion" | "timeline" | "speech">(
    "conclusion"
  );
  const [showContributors, setShowContributors] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  return (
    <BaseChartBlock
      attemptId={attemptId}
      reportType="transcribe"
      titleKey="label-transcribe-report"
      onAnalyticsData={onAnalyticsData}
    >
      {(report) => {
        const counts = report.counts as TranscribeCounts;
        const totalSegments = counts?.total_segments ?? 0;
        const dialogueSegments = counts?.dialogue_segments ?? 0;
        const monologueSegments = counts?.monologue_segments ?? 0;
        const suspiciousSegments = counts?.suspicious_segments ?? 0;
        const summary =
          locale === "en"
            ? getSummaryContent(report.summary_en)
            : locale === "kz"
              ? getSummaryContent(report.summary_kz)
              : getSummaryContent(report.summary_ru);

        const conclusionText = (() => {
          if (!summary) return "";
          const lines = summary.split("\n").map((line) => line.trim()).filter(Boolean);
          const filtered = lines.filter(
            (line) => !["low", "medium", "high", "critical"].includes(line.toLowerCase())
          );
          return filtered.slice(0, 3).join(" ");
        })();

        const graphData = report.graph_data as {
          timeline?: {
            patterns?: Array<{
              time_sec?: number;
              action_type?: string;
              weight?: string;
              evidence?: string;
            }>;
          };
        } | null;

        const patterns = graphData?.timeline?.patterns ?? [];
        const groupedPatterns = patterns.reduce<Record<string, typeof patterns>>(
          (acc, pattern) => {
            const key = pattern.action_type || "other";
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(pattern);
            return acc;
          },
          {}
        );

        const contributors = (report as { contributors?: Array<{ action_type: string; count: number; points: number; share: number }> })
          .contributors ?? [];
        const sortedContributors = [...contributors].sort(
          (a, b) => (b.points ?? 0) - (a.points ?? 0)
        );

        const pieSlices = (() => {
          if (sortedContributors.length === 0) return [];
          const cx = 100;
          const cy = 100;
          const radius = 90;
          let currentAngle = -90;
          return sortedContributors.map((item) => {
            const angleSize = (item.share ?? 0) * 360;
            const startAngle = currentAngle;
            const endAngle = currentAngle + angleSize;
            const path =
              angleSize >= 359.999
                ? describeFullCircle(cx, cy, radius)
                : describePieSlice(cx, cy, radius, startAngle, endAngle);
            currentAngle = endAngle;
            return {
              path,
              color: getTranscribeColor(item.action_type),
            };
          });
        })();

        const rawData = (report as { raw_data?: { segments?: Array<{ start?: number; end?: number; text?: string }> } })
          .raw_data;
        const segments = rawData?.segments ?? [];
        const maxSegments = 150;
        const visibleSegments = segments.slice(0, maxSegments);
        const groupKeys = Object.keys(groupedPatterns);
        const hasTimelinePatterns = patterns.length > 0;
        const hasRawSegments = segments.length > 0;
        const hasContributors = sortedContributors.length > 0;

        const toggleGroup = (type: string) => {
          setExpandedGroups((prev) => ({
            ...prev,
            [type]: !prev[type],
          }));
        };

        return (
          <CardContent className="flex-1 flex flex-col items-start min-h-0 p-0! gap-4 max-h-[70vh] overflow-y-auto pr-2">
            <Divider />
            <div className="grid grid-cols-2 w-full gap-2.5 lg:grid-cols-4">
              <StatCard
                label={t("label-total-segments")}
                value={totalSegments}
              />
              <StatCard
                label={t("label-dialogue-segments")}
                value={dialogueSegments}
              />
              <StatCard
                label={t("label-monologue-segments")}
                value={monologueSegments}
              />
              <StatCard
                label={t("label-suspicious-segments")}
                value={suspiciousSegments}
                variant="warning"
              />
            </div>

            {hasContributors && (
              <div className="w-full rounded-lg border border-[#E5E7EB]">
                <button
                  type="button"
                  onClick={() => setShowContributors((prev) => !prev)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB] text-left"
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#374151]">
                    <span>{t("label-violation-distribution")}</span>
                  </div>
                  <span
                    className={`text-xs text-[#6B7280] transition-transform ${showContributors ? "rotate-180" : ""
                      }`}
                  >
                    <ChevronDownIcon />
                  </span>
                </button>

                {showContributors && (
                  <div className="p-5">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-[200px_1fr] items-start">
                      <div className="w-[200px] h-[200px] mx-auto">
                        <svg viewBox="0 0 200 200" className="w-full h-full">
                          {pieSlices.map((slice, index) => (
                            <g key={index}>
                              <path
                                d={slice.path}
                                fill={slice.color}
                                stroke="#fff"
                                strokeWidth={2}
                                className="cursor-pointer transition-[opacity,transform] duration-150"
                                style={{
                                  opacity: hoveredIndex === null || hoveredIndex === index ? 1 : 0.6,
                                  transform: hoveredIndex === index ? "scale(1.02)" : "scale(1)",
                                  transformOrigin: "center",
                                  filter: hoveredIndex === index ? "drop-shadow(0 0 8px rgba(0,0,0,0.3))" : "none",
                                }}
                                onMouseEnter={() => setHoveredIndex(index)}
                                onMouseLeave={() => setHoveredIndex(null)}
                              />
                            </g>
                          ))}
                        </svg>
                      </div>

                      <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2">
                        {sortedContributors.map((item, index) => (
                          <div
                            key={`${item.action_type}-${index}`}
                            className={`flex items-center gap-2 rounded-md border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 transition-all cursor-pointer ${hoveredIndex === index
                              ? "shadow-md scale-[1.02] border-[#3B82F6] bg-[#EFF6FF]"
                              : "hover:shadow"
                              }`}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                          >
                            <div
                              className="min-w-[50px] h-8 rounded flex items-center justify-center text-white text-sm font-bold px-2"
                              style={{ background: getTranscribeColor(item.action_type) }}
                            >
                              {formatPercent(item.share)}
                            </div>
                            <div className="flex flex-col gap-0.5">
                              <div className="text-[13px] font-semibold text-[#1F2937] leading-snug">
                                {getTranscribeLabel(item.action_type, t)}
                              </div>
                              <div className="text-[11px] text-[#6B7280] font-medium">
                                {item.count} {t("label-count-times")}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {(conclusionText || hasTimelinePatterns || hasRawSegments) && (
              <div className="w-full rounded-lg border border-[#E5E7EB]">
                <div className="flex border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <button
                    type="button"
                    onClick={() => setActiveTab("conclusion")}
                    className={`cursor-pointer flex-1 flex items-center justify-center gap-1.5 px-4 py-3 text-[13px] font-semibold border-b-3 transition-all ${activeTab === "conclusion"
                      ? "text-[#3B82F6] bg-white border-[#3B82F6]"
                      : "text-[#6B7280] border-transparent hover:bg-[#F3F4F6]"
                      }`}
                  >
                    {t("label-conclusion")}
                  </button>
                  {hasTimelinePatterns && (
                    <button
                      type="button"
                      onClick={() => setActiveTab("timeline")}
                      className={`cursor-pointer flex-1 flex items-center justify-center gap-1.5 px-4 py-3 text-[13px] font-semibold border-b-3 transition-all ${activeTab === "timeline"
                        ? "text-[#3B82F6] bg-white border-[#3B82F6]"
                        : "text-[#6B7280] border-transparent hover:bg-[#F3F4F6]"
                        }`}
                    >
                      {t("label-timeline")} ({patterns.length})
                    </button>
                  )}
                  {hasRawSegments && (
                    <button
                      type="button"
                      onClick={() => setActiveTab("speech")}
                      className={`cursor-pointer flex-1 flex items-center justify-center gap-1.5 px-4 py-3 text-[13px] font-semibold border-b-3 transition-all ${activeTab === "speech"
                        ? "text-[#3B82F6] bg-white border-[#3B82F6]"
                        : "text-[#6B7280] border-transparent hover:bg-[#F3F4F6]"
                        }`}
                    >
                      {t("label-speech-log")} ({segments.length})
                    </button>
                  )}
                </div>

                <div className="p-5">
                  {activeTab === "conclusion" && (
                    <div className="text-sm text-[#374151] leading-7">
                      {conclusionText || t("label-report-empty")}
                    </div>
                  )}

                  {activeTab === "timeline" && hasTimelinePatterns && (
                    <div className="flex flex-col gap-3">
                      {groupKeys.map((key) => {
                        const group = groupedPatterns[key];
                        const isExpanded = expandedGroups[key] ?? true;
                        return (
                          <div key={key} className="border border-[#E5E7EB] rounded-md overflow-hidden">
                            <button
                              type="button"
                              onClick={() => toggleGroup(key)}
                              className="w-full flex items-center justify-between px-4 py-3 bg-[#F9FAFB] text-left"
                            >
                              <div className="flex items-center gap-3">
                                <span
                                  className="inline-flex items-center px-3 py-1 rounded text-white text-xs font-semibold"
                                  style={{ background: getTranscribeColor(key) }}
                                >
                                  {getTranscribeLabel(key, t)}
                                </span>
                                <span className="text-xs text-[#6B7280] font-medium">
                                  {group.length} {t("label-occurrences")}
                                </span>
                              </div>
                              <span
                                className={`text-xs text-[#6B7280] transition-transform ${isExpanded ? "rotate-180" : ""
                                  }`}
                              >
                                <ChevronDownIcon />
                              </span>
                            </button>
                            {isExpanded && (
                              <div className="px-4 py-3 bg-white border-t border-[#E5E7EB]">
                                {group.map((pattern, index) => (
                                  <div
                                    key={`${key}-${index}`}
                                    className="flex items-start gap-3 py-2 border-b border-[#F3F4F6] last:border-b-0"
                                  >
                                    <div className="min-w-[60px] text-xs font-semibold text-[#3B82F6]">
                                      {formatTime(pattern.time_sec)}
                                    </div>
                                    <div className="text-[13px] text-[#374151] leading-relaxed flex-1">
                                      {pattern.evidence}
                                    </div>
                                    {pattern.weight && (
                                      <div className="shrink-0">
                                        <span
                                          className={`text-[10px] uppercase px-2 py-0.5 rounded ${weightBadgeClass(
                                            pattern.weight
                                          )}`}
                                        >
                                          {pattern.weight}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {activeTab === "speech" && hasRawSegments && (
                    <div>
                      {visibleSegments.map((segment, index) => (
                        <div key={index} className="py-3 border-b border-[#E5E7EB] last:border-b-0">
                          <div className="flex items-center justify-between text-xs font-semibold text-[#3B82F6] border-b border-[#F3F4F6] pb-1.5 mb-2">
                            {formatTime(segment.start)} - {formatTime(segment.end)}
                          </div>
                          <div className="text-[13px] text-[#374151] leading-relaxed">
                            {segment.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        );
      }}
    </BaseChartBlock>
  );
});
